import { Row } from '@tanstack/react-table'
import { useEffect, useRef, useState } from 'react'

import { DBLTable } from '@ui/components/elements'
import { AttributeGroup, DatableTrace, KeyValue, ServiceGroup, TracesGroupBy, TraceViewStates } from '@ui/interfaces'
import { useTraceExplorerData } from '@ui/lib/hooks/useData'
import { useTraceQueries } from '@ui/lib/hooks/useQueries'
import { AttributeGraphGroup } from '@ui/pages/explore/components/AttributeGraphGroup'
import { TraceFlyout } from '@ui/pages/explore/components/TraceFlyout'
import { TraceGroupTableHeader } from '@ui/pages/explore/components/TraceGroupTableHeader'
import dayjs from 'dayjs'

export const TracesExplorer = () => {
  const [groupBy, setGroupBy] = useState<TracesGroupBy>(TracesGroupBy.SERVICE)
  const [traceViewState, setTraceViewState] = useState<TraceViewStates>(TraceViewStates.GROUP)
  const [selectedGroup, setSelectedGroup] = useState<ServiceGroup | null>(null)
  const [selectedAttribute, setSelectedAttribute] = useState<{ name: string; value: string } | null>(null)
  const [selectedRootTrace, setSelectedRootTrace] = useState<DatableTrace | null>(null)
  const [whereClauseServices, setWhereClauseServices] = useState<string>('')
  const [whereClauseAttributes, setWhereClauseAttributes] = useState<string>('')
  const [timeRange] = useState({
    startTime: dayjs().subtract(5, 'minute').toISOString(),
    endTime: dayjs().toISOString(),
  })
  const [showFlyout, setShowFlyout] = useState(false)
  const [orderBy, setOrderBy] = useState<{ column: string; direction: string }>({
    column: 'total_size',
    direction: 'desc',
  })

  const {
    traceServiceGroupLoading,
    traceServiceGroupQueryData,
    spansByServiceLoading,
    spansByServiceQueryData,
    fullTraceQueryData,
    spanAttributesLoading,
    spanAttributesQueryData,
    tracesWithAttributeValueLoading,
    tracesWithAttributeValueQueryData,
  } = useTraceQueries({
    timeRange,
    whereClauseServices,
    whereClauseAttributes,
    orderBy,
    selectedGroup,
    selectedRootTrace,
    attribute: selectedAttribute?.name,
    value: selectedAttribute?.value,
  })

  const { serviceGroupData, rootTraceData, fullTraceData, attributeGroupData, tracesWithAttributeValueData } =
    useTraceExplorerData({
      traceServiceGroupQueryData,
      spansByServiceQueryData,
      fullTraceQueryData,
      spanAttributesQueryData,
      tracesWithAttributeValueQueryData,
    })

  // Store the data in a ref so that we can access it w
  const serviceGroupDataRef = useRef<ServiceGroup[]>([])
  const attributeGroupDataRef = useRef<AttributeGroup[]>([])

  useEffect(() => {
    if (serviceGroupData.length > 0) {
      // Data gets reshaped in the memo, so we need to cast it back to the original type
      serviceGroupDataRef.current = serviceGroupData as unknown as ServiceGroup[]
    }

    if (attributeGroupData.length > 0) {
      attributeGroupDataRef.current = attributeGroupData as AttributeGroup[]
    }
  }, [attributeGroupData, serviceGroupData])

  const attributeData = attributeGroupData.length === 0 ? attributeGroupDataRef.current : attributeGroupData
  const serviceData = serviceGroupData.length === 0 ? serviceGroupDataRef.current : serviceGroupData

  const handleGroupRowClick = ({ row }: { row: Row<KeyValue> }) => {
    const group: ServiceGroup = Object.keys(row.original).reduce((acc) => {
      return {
        ...acc,
        serviceName: row.original['Service Name'],
        name: row.original.Name,
        totalSize: row.original['Total Size'],
        count: row.original.Count,
      }
    }, {} as ServiceGroup)
    setSelectedGroup(group)
    setShowFlyout(false)
    setTraceViewState(TraceViewStates.ROOT_TRACES)
  }

  const handleAttributeClick = (attribute: { name: string; value: string }) => {
    setSelectedAttribute(attribute)
    setShowFlyout(false)
    setTraceViewState(TraceViewStates.ROOT_TRACES)
  }

  const handleRootTraceRowClick = ({ row }: { row: Row<KeyValue> }) => {
    setSelectedRootTrace(row.original as DatableTrace)
    setShowFlyout(true)
  }

  const handleTraceViewChange = (viewState: TraceViewStates) => {
    const rootTableData = groupBy === TracesGroupBy.ATTRIBUTE ? tracesWithAttributeValueData : rootTraceData
    const rootTableLoading =
      groupBy === TracesGroupBy.ATTRIBUTE ? tracesWithAttributeValueLoading : spansByServiceLoading
    const currentGroup = groupBy === TracesGroupBy.ATTRIBUTE ? selectedAttribute : (selectedGroup ?? null)
    const setWhereClause = groupBy === TracesGroupBy.SERVICE ? setWhereClauseServices : setWhereClauseAttributes

    switch (viewState) {
      case TraceViewStates.GROUP:
        if (groupBy === TracesGroupBy.ATTRIBUTE) {
          return (
            <div className="border border-gray-200 bg-white">
              <div className="flex items-start justify-start p-2.5">
                {
                  <TraceGroupTableHeader
                    traceViewState={traceViewState}
                    setTraceViewState={setTraceViewState}
                    groupBy={groupBy}
                    setGroupBy={setGroupBy}
                    setWhereClause={setWhereClause}
                    selectedGroup={currentGroup}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                    loading={traceServiceGroupLoading}
                    count={attributeGroupData.length}
                  />
                }
              </div>
              <AttributeGraphGroup
                loading={spanAttributesLoading}
                attributeGroupData={attributeData}
                handleClick={handleAttributeClick}
              />
            </div>
          )
        } else {
          return (
            <DBLTable
              tableData={serviceData}
              dataSource="trace groups"
              tableRowClick={handleGroupRowClick}
              customTableHeader={
                <TraceGroupTableHeader
                  traceViewState={traceViewState}
                  setTraceViewState={setTraceViewState}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  setWhereClause={setWhereClause}
                  selectedGroup={currentGroup}
                  orderBy={orderBy}
                  setOrderBy={setOrderBy}
                  loading={traceServiceGroupLoading}
                  count={serviceGroupData.length}
                />
              }
              showAddColumns={false}
              defaultColumnOrder={['Service Name', 'Name', 'Total Size', 'Count']}
              loading={traceServiceGroupLoading}
            />
          )
        }

      case TraceViewStates.ROOT_TRACES:
        return (
          <>
            <DBLTable
              tableData={rootTableData}
              dataSource="custom"
              tableRowClick={handleRootTraceRowClick}
              defaultColumnOrder={[
                'timestamp',
                'resource.service.name',
                'name',
                'trace_id',
                'span_id',
                `resource.${selectedAttribute?.name}`,
                `attributes.${selectedAttribute?.name}`,
                `${selectedAttribute?.name}`,
              ]}
              customTableHeader={
                <TraceGroupTableHeader
                  traceViewState={traceViewState}
                  setTraceViewState={setTraceViewState}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  setWhereClause={setWhereClause}
                  selectedGroup={currentGroup}
                  loading={rootTableLoading}
                  count={rootTableData.length}
                />
              }
              loading={rootTableLoading}
            />
            <TraceFlyout
              show={showFlyout}
              setShow={setShowFlyout}
              selectedRootTrace={selectedRootTrace}
              setSelectedRootTrace={setSelectedRootTrace}
              fullTrace={fullTraceData}
            />
          </>
        )
    }
  }

  return <div className="size-full">{handleTraceViewChange(traceViewState)}</div>
}
